import { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef } from "react";
import { getSessionId, trackCart } from "../lib/track";
import { DEFAULT_SPICE } from "../lib/spice";

const TrayContext = createContext(null);

const keyFor = (name, size) => `${name}|${size}`;
export const DELIVERY_FEE = 15;

export function TrayProvider({ children }) {
  const [items, setItems] = useState({});
  const [eventDate, setEventDate] = useState(null);
  const [fulfillment, setFulfillment] = useState("Pickup");
  const sessionId = getSessionId();

  const add = useCallback((dish, size) => {
    const price = size === "Full Tray" ? dish.full : dish.half;
    const key = keyFor(dish.name, size);
    setItems((prev) => {
      const existing = prev[key];
      return {
        ...prev,
        [key]: existing
          ? { ...existing, qty: existing.qty + 1 }
          : { name: dish.name, size, price, qty: 1, spice: DEFAULT_SPICE },
      };
    });
  }, []);

  const setQty = useCallback((key, qty) => {
    setItems((prev) => {
      if (qty <= 0) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: { ...prev[key], qty } };
    });
  }, []);

  const setSpice = useCallback((key, spice) => {
    setItems((prev) => (prev[key] ? { ...prev, [key]: { ...prev[key], spice } } : prev));
  }, []);

  const remove = useCallback((key) => {
    setItems((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setItems({});
    setEventDate(null);
    setFulfillment("Pickup");
  }, []);

  const list = useMemo(() => Object.entries(items).map(([key, v]) => ({ key, ...v })), [items]);
  const subtotal = useMemo(() => list.reduce((s, i) => s + i.price * i.qty, 0), [list]);
  const count = useMemo(() => list.reduce((s, i) => s + i.qty, 0), [list]);
  const deliveryFee = fulfillment === "Delivery" && count > 0 ? DELIVERY_FEE : 0;
  const total = subtotal + deliveryFee;

  // Track cart activity (debounced) so the admin can see added/abandoned carts.
  const timer = useRef(null);
  useEffect(() => {
    if (count === 0) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      trackCart({
        items: list.map(({ name, size, price, qty, spice }) => ({ name, size, price, qty, spice })),
        subtotal,
        total,
        fulfillment,
      });
    }, 1200);
    return () => timer.current && clearTimeout(timer.current);
  }, [list, subtotal, total, fulfillment, count]);

  const value = {
    items, list, subtotal, deliveryFee, total, count, sessionId,
    add, setQty, setSpice, remove, clear, keyFor,
    eventDate, setEventDate, fulfillment, setFulfillment,
  };
  return <TrayContext.Provider value={value}>{children}</TrayContext.Provider>;
}

export function useTray() {
  const ctx = useContext(TrayContext);
  if (!ctx) throw new Error("useTray must be used within TrayProvider");
  return ctx;
}
